import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    Button,
    IconButton,
    TextField,
    MenuItem,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    Tooltip,
} from '@mui/material';
import { Close, Download, Save, PersonAdd } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';

const ATTENDANCE_STATUS = ['enrolled', 'attended', 'absent', 'withdrawn'];
const ENROLLED_AS_OPTIONS = ['trainee', 'instructor', 'admin'];

const ATTENDANCE_COLOR = {
    enrolled:  'default',
    attended:  'success',
    absent:    'warning',
    withdrawn: 'error',
};

const ENROLLED_AS_COLOR = {
    trainee:    'primary',
    instructor: 'secondary',
    admin:      'warning',
};

const GRID_CHIP_SX = {
    width: 88,
    height: 20,
    cursor: 'pointer',
    '& .MuiChip-label': { fontSize: '0.69rem', px: 0.75 },
};

const ensureResults = (enrs, subjs) =>
    enrs.map((e) => ({
        ...e,
        results: subjs.map((s) => {
            const existing = (e.results || []).find(
                (r) => r.subjectId === s._id || r.subjectId?._id === s._id
            );
            return existing || { subjectId: s._id, score: null, passed: null, grade: '', notes: '' };
        }),
    }));

const PassedChip = ({ passed, onClick }) => {
    const label   = passed === true ? 'P' : passed === false ? 'F' : '–';
    const color   = passed === true ? 'success' : passed === false ? 'error' : 'default';
    const variant = passed === null || passed === undefined ? 'outlined' : 'filled';
    return (
        <Chip
            label={label}
            size="small"
            color={color}
            variant={variant}
            onClick={onClick}
            sx={{ height: 18, cursor: 'pointer', '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }}
        />
    );
};

/* ── Attendance + Results Panel ─────────────────────── */

const AttendanceResultsPanel = ({ subjects, enrollments, onEnrollmentsChange, employees }) => {
    const [addPerson, setAddPerson] = useState(null);

    const handleAddParticipant = () => {
        if (!addPerson) return;
        if (enrollments.some((e) => e.employeeCode === addPerson.employeeCode)) {
            toast.error('Person already in list.');
            return;
        }
        onEnrollmentsChange([
            ...enrollments,
            {
                employeeCode: addPerson.employeeCode,
                employeeName: addPerson.employeeName || '',
                rank:         addPerson.rank          || '',
                base:         addPerson.homeBase      || '',
                enrolledAs:   'trainee',
                status:       'enrolled',
                results:      subjects.map((s) => ({ subjectId: s._id, score: null, passed: null, grade: '', notes: '' })),
            },
        ]);
        setAddPerson(null);
    };

    const handleStatusChange = (employeeCode, status) => {
        const clearResults = status === 'absent' || status === 'withdrawn';
        onEnrollmentsChange(enrollments.map((e) => {
            if (e.employeeCode !== employeeCode) return e;
            return {
                ...e,
                status,
                ...(clearResults ? {
                    results: subjects.map((s) => ({
                        subjectId: s._id,
                        score: null,
                        passed: false,
                        grade: '',
                        notes: ''
                    })),
                } : {}),
            };
        }));
    };

    const updateEnrolledAs = (employeeCode, enrolledAs) =>
        onEnrollmentsChange(enrollments.map((e) =>
            e.employeeCode === employeeCode ? { ...e, enrolledAs } : e
        ));

    const updateResult = (employeeCode, subjectId, patch) => {
        onEnrollmentsChange(enrollments.map((e) => {
            if (e.employeeCode !== employeeCode) return e;
            return {
                ...e,
                results: e.results.map((r) =>
                    (r.subjectId === subjectId || r.subjectId?._id === subjectId)
                        ? { ...r, ...patch }
                        : r
                ),
            };
        }));
    };

    const handleScoreChange = (employeeCode, subject, scoreValue) => {
        const score  = scoreValue === '' ? null : Number(scoreValue);
        const passed = score !== null && subject.passingScore != null ? score >= subject.passingScore : null;
        updateResult(employeeCode, subject._id, { score, passed });
    };

    const handlePassedChange = (employeeCode, subjectId, passed) =>
        updateResult(employeeCode, subjectId, { passed });

    const handleRemove = (employeeCode) =>
        onEnrollmentsChange(enrollments.filter((e) => e.employeeCode !== employeeCode));

    const subjectColumns = subjects.map((s) => ({
        field: `score_${s._id}`,
        headerName: s.subjectCode,
        width: 110,
        sortable: false,
        renderCell: (params) => {
            const result       = (params.row.results || []).find((r) => r.subjectId === s._id || r.subjectId?._id === s._id);
            const isTrainee    = (params.row.enrolledAs || 'trainee') === 'trainee';
            const statusLocked = !isTrainee || params.row.status === 'absent' || params.row.status === 'withdrawn';
            const nextPassed   = result?.passed === true ? false : result?.passed === false ? null : true;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%', width: '100%' }}>
                    <TextField
                        type="number" size="small" variant="standard"
                        disabled={statusLocked}
                        value={result?.score ?? ''}
                        onChange={(e) => handleScoreChange(params.row.employeeCode, s, e.target.value)}
                        slotProps={{ htmlInput: { min: 0, max: 100, step: 0.1, style: { width: 45, textAlign: 'center' } } }}
                    />
                    <PassedChip
                        passed={result?.passed ?? null}
                        onClick={() => handlePassedChange(params.row.employeeCode, s._id, nextPassed)}
                    />
                </Box>
            );
        },
    }));

    const columns = [
        {
            field: 'no', 
            headerName: '#', 
            width: 48, 
            sortable: false,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
        },
        {
            field: 'employeeCode', 
            headerName: 'Code', 
            width: 60,            
        },
        {
            field: 'employeeName',
            headerName: 'EmployeeName',
            flex: 1,
            minWidth: 180,
            renderCell: (params) => params.row.employeeName || params.row.fullName || '',
        },
        {
            field: 'rank',
            headerName: 'Rank',
            width: 65
        },
        {
            field: 'base',
            headerName: 'Base',
            width: 60
        },
        {
            field: 'enrolledAs',
            headerName: 'Role',
            width: 110,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                    <TextField
                        select size="small" variant="standard"
                        value={params.value || 'trainee'}
                        onChange={(e) => updateEnrolledAs(params.row.employeeCode, e.target.value)}
                        sx={{ width: 96 }}
                    >
                        {ENROLLED_AS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>
                                <Chip
                                    label={s.charAt(0).toUpperCase() + s.slice(1)}
                                    size="small"
                                    color={ENROLLED_AS_COLOR[s] || 'default'}
                                    sx={GRID_CHIP_SX}
                                />
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            ),
        },
        {
            field: 'status',
            headerName: 'Status', 
            width: 120, 
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
                    <TextField
                        select size="small" variant="standard"
                        value={params.value || 'enrolled'}
                        onChange={(e) => handleStatusChange(params.row.employeeCode, e.target.value)}
                        sx={{ width: 88 }}
                    >
                        {ATTENDANCE_STATUS.map((s) => (
                            <MenuItem key={s} value={s}>
                                <Chip
                                    label={s.charAt(0).toUpperCase() + s.slice(1)}
                                    size="small"
                                    color={ATTENDANCE_COLOR[s] || 'default'}
                                    sx={GRID_CHIP_SX}
                                />
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            ),
        },
        ...subjectColumns,
        {
            field: '_remove', 
            headerName: '', 
            width: 60, 
            sortable: false,
            renderCell: (params) => (
                <IconButton 
                    color="error" 
                    onClick={() => handleRemove(params.row.employeeCode)}
                >
                    <Close />
                </IconButton>
            ),
        },
    ];

    return (
        <Box display="flex" flexDirection="column" height="100%" gap={1.5}>            
            {/* Toolbar — Add participant */}
            <Box display="flex" gap={1} alignItems="center">
                <Autocomplete
                    size="small"
                    sx={{ flex: 1 }}
                    options={employees
                        .filter((e) => !enrollments.some((en) => en.employeeCode === e.employeeCode))
                        .sort((a, b) => Number(a.employeeCode) - Number(b.employeeCode))}
                    getOptionLabel={(o) => `${o.employeeCode} - ${o.employeeName || ''}`}
                    value={addPerson}
                    onChange={(_, v) => setAddPerson(v)}
                    isOptionEqualToValue={(o, v) => o.employeeCode === v?.employeeCode}
                    renderInput={(params) => <TextField {...params} label="Add participant" />}
                    noOptionsText="No employees found"
                />
                <IconButton size="small" color="primary" onClick={handleAddParticipant} disabled={!addPerson}>
                    <PersonAdd />
                </IconButton>
            </Box>

            {/* Grid */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataGrid
                    getRowId={(row) => row.employeeCode}
                    rows={enrollments}
                    columns={columns}
                    autoHeight={false}
                    hideFooter={enrollments.length <= 100}
                    disableRowSelectionOnClick
                    sx={{
                        height: '100%',
                        '& .MuiDataGrid-cell': { alignItems: 'center' },
                    }}
                />
            </Box>
        </Box>
    );
};


/* ── SessionAttendanceDialog ─────────────────────────── */

const SessionAttendanceDialog = ({ open, session, onClose, onSaved }) => {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [sessionData, setSessionData] = useState(null);
    const [enrollments, setEnrollments] = useState([]);
    const [employees, setEmployees]     = useState([]);
    const [loading, setLoading]         = useState(false);
    const [importing, setImporting]     = useState(false);
    const [saving, setSaving]           = useState(false);

    const fetchSession = useCallback(async () => {
        if (!session?._id) return;
        setLoading(true);
        try {
            const res = await api.get(`/trainingsessions/${session._id}`);
            setSessionData(res.data);
        } catch {
            toast.error('Failed to load session data.');
        } finally {
            setLoading(false);
        }
    }, [session?._id]);

    useEffect(() => {
        if (open) {
            fetchSession();
            api.get('/employees/active').then((r) => setEmployees(r.data || []));
        }
    }, [open, fetchSession]);

    useEffect(() => {
        if (!sessionData) return;
        const subs = sessionData.subjects || [];
        setEnrollments(ensureResults(sessionData.enrollments || [], subs));
    }, [sessionData]);

    const subjects = sessionData?.subjects || session?.subjects || [];

    const handleImport = async () => {
        setImporting(true);
        try {
            const res = await api.post(`/trainingsessions/${session?._id}/import-attendees`);
            const subs = res.data.session?.subjects || subjects;
            setEnrollments(ensureResults(res.data.session?.enrollments || [], subs));
            toast.success(`Imported ${res.data.added} attendee(s) from course.`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Import failed.');
        } finally {
            setImporting(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.post(`/trainingsessions/${session?._id}/results`, { enrollments });
            toast.success('Saved successfully.');
            fetchSession();
            onSaved();
            return true;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save!');
            return false;
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            slotProps={{ paper: { sx: { height: '100%', display: 'flex', flexDirection: 'column' } } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <FlexBetween alignItems="flex-start">
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Attendance & Results
                        </Typography>
                        <Box display="flex" flexWrap="wrap" alignItems="center" gap={0.5} mt={0.25}>
                            <Typography variant="caption" color="text.secondary">
                                {session?.startDate ? format(new Date(session.startDate), 'dd/MM/yyyy') : '—'}
                                {session?.endDate ? ` → ${format(new Date(session.endDate), 'dd/MM/yyyy')}` : ''}
                            </Typography>
                            {subjects.map((s) => (
                                <Chip key={s._id} label={s.subjectCode} size="small" variant="outlined" />
                            ))}
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title="Import attendees from linked course" arrow>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleImport}
                                    disabled={importing || !session?._id}
                                >
                                    {importing ? <CircularProgress size={16} /> : <Download />}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <IconButton size="small" onClick={onClose}>
                            <Close />
                        </IconButton>
                    </Box>
                </FlexBetween>
            </DialogTitle>

            <DialogContent
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    px: { xs: 1.5, sm: 2.5 },
                    pt: 1,
                }}
            >
                {loading ? (
                    <FlexCenter height={200}><CircularProgress /></FlexCenter>
                ) : (
                    <AttendanceResultsPanel
                        subjects={subjects}
                        enrollments={enrollments}
                        onEnrollmentsChange={setEnrollments}
                        employees={employees}
                    />
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                    onClick={async () => {
                        const success = await handleSave();
                        if (success) onClose();
                    }}
                    disabled={saving}
                >
                    Save
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={onClose}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionAttendanceDialog;
